import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./NewLanding.css";

import myPlanShot from "./assets/my-plan.png";
import launcherShot from "./assets/launcher.png";
import foodMatchShot from "./assets/food-match.png";
import skipWhyShot from "./assets/skip-why.png";
import comparisonShot from "./assets/comparison.png";
import vetChatShot from "./assets/vet-chat.png";

import appStoreBadge from "../../apstore.svg";
import googlePlayBadge from "../../googleplay.svg";
import logo from "../../logo.svg";

const APP_STORE_URL = "https://apps.apple.com/ua/app/pawplate/id6746093497";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=io.pawplate.app.twa";

const StoreBadges = ({ size = "md" }) => (
  <div className={`nl-store-badges nl-store-badges--${size}`}>
    <a href={APP_STORE_URL} target="_blank" rel="noreferrer" aria-label="Download on the App Store">
      <img src={appStoreBadge} alt="Download on the App Store" />
    </a>
    <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" aria-label="Get it on Google Play">
      <img src={googlePlayBadge} alt="Get it on Google Play" />
    </a>
  </div>
);

const Phone = ({ src, alt, className = "", tilt }) => (
  <figure className={`nl-phone ${tilt ? `nl-phone--${tilt}` : ""} ${className}`}>
    <div className="nl-phone-body">
      <div className="nl-phone-notch" aria-hidden="true" />
      <img src={src} alt={alt} loading="lazy" />
    </div>
  </figure>
);

const PawMark = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true" className="nl-pawmark">
    <ellipse cx="22" cy="20" rx="7" ry="9" />
    <ellipse cx="42" cy="20" rx="7" ry="9" />
    <ellipse cx="10.5" cy="34" rx="6" ry="7.5" />
    <ellipse cx="53.5" cy="34" rx="6" ry="7.5" />
    <path d="M32 30c-9 0-17 7.5-17 15.5C15 52 20 56 26.5 56c2.4 0 4-.7 5.5-.7s3.1.7 5.5.7C44 56 49 52 49 45.5 49 37.5 41 30 32 30z" />
  </svg>
);

const NewLanding = () => {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-revealed");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.16 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="nl">
      {/* ───────────────────────── NAV */}
      <header className="nl-nav">
        <div className="nl-shell nl-nav-inner">
          <a className="nl-brand" href="/" aria-label="PawPlate home">
            <img src={logo} alt="" className="nl-brand-mark" />
            <span className="nl-brand-word">PawPlate</span>
          </a>
          <div className="nl-nav-cta">
            <a className="nl-btn nl-btn--ghost" href="https://app.pawplate.io/" target="_blank" rel="noreferrer">
              Sign in
            </a>
            <a className="nl-btn nl-btn--primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              Get the app
            </a>
          </div>
        </div>
      </header>

      {/* ───────────────────────── HERO — wellness & care first */}
      <section className="nl-hero">
        <div className="nl-hero-wash" aria-hidden="true" />
        <PawMark />
        <div className="nl-shell nl-hero-grid">
          <div className="nl-hero-copy" data-reveal>
            <p className="nl-eyebrow">Pet wellness &amp; care</p>
            <h1 className="nl-display">
              All the care
              <br />
              they deserve.
              <br />
              <em>Made personal.</em>
            </h1>
            <p className="nl-lede">
              PawPlate is a daily wellbeing companion for your pet — nutrition
              that fits, gentle daily guidance, health you can keep an eye on,
              and a plan built around the one you love. Not averages. Not
              generic advice. <strong>Your pet.</strong>
            </p>
            <StoreBadges size="lg" />
            <p className="nl-trustline">
              Guidance built on WSAVA · AAFCO · PDSA veterinary standards.
              Screening and care navigation — never a diagnosis.
            </p>
          </div>

          <div className="nl-hero-stage" data-reveal>
            <div className="nl-chip nl-chip--float nl-chip--one">
              🐱 Milo · Daily plan
            </div>
            <div className="nl-chip nl-chip--float nl-chip--two">
              <span className="nl-chip-dot" aria-hidden="true" /> Lesson done · streak +1
            </div>
            <Phone src={myPlanShot} alt="Milo's personal daily wellness plan in PawPlate" className="nl-hero-phone" />
          </div>
        </div>
      </section>

      {/* ───────────────────────── CASE A — THE SCANNER */}
      <section className="nl-case nl-case--tinted">
        <div className="nl-shell nl-case-grid">
          <div className="nl-case-copy" data-reveal>
            <p className="nl-kicker">And when you're unsure…</p>
            <h2 className="nl-h2">
              Point the camera.
              <br />
              <em>PawPlate reads it.</em>
            </h2>
            <p className="nl-body">
              Food labels, meals, symptoms, posture, coat — one scanner, 16
              kinds of checks. Every result is matched to your pet's profile
              and comes back as a plain-words verdict with a clear next step.
            </p>
            <ul className="nl-points">
              <li>
                <strong>Food &amp; labels</strong> — is this right for your pet,
                before it goes in the cart?
              </li>
              <li>
                <strong>Health worries</strong> — vomit, stool, skin, eyes, ears,
                wounds… triaged calmly and honestly.
              </li>
              <li>
                <strong>Your pet themself</strong> — posture, coat and mood,
                tracked over time.
              </li>
            </ul>
          </div>
          <div className="nl-case-stage" data-reveal>
            <Phone src={launcherShot} alt="PawPlate Health Scanner home screen" tilt="right" />
          </div>
        </div>
      </section>

      {/* ───────────────────────── VERDICT STRIP */}
      <section className="nl-verdicts" aria-hidden="true">
        <div className="nl-verdicts-track">
          {[...Array(2)].map((_, i) => (
            <div className="nl-verdicts-run" key={i}>
              <span>Looks promising.</span>
              <span className="nl-vd-sep">·</span>
              <span>Skip this one.</span>
              <span className="nl-vd-sep">·</span>
              <span>Practically equal — either works.</span>
              <span className="nl-vd-sep">·</span>
              <span>Scan the back to confirm.</span>
              <span className="nl-vd-sep">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────────────────── CASE B — PERSONAL MATCH */}
      <section className="nl-case">
        <div className="nl-shell nl-case-grid nl-case-grid--flip">
          <div className="nl-case-stage" data-reveal>
            <Phone src={foodMatchShot} alt="Personalized food match result for Milo" tilt="left" />
          </div>
          <div className="nl-case-copy" data-reveal>
            <p className="nl-kicker">In the store · at home</p>
            <h2 className="nl-h2">
              “Is this food right for <em>my</em> cat?”
            </h2>
            <p className="nl-body">
              Every rating is computed against the pet you actually have — not
              a generic “cat”. PawPlate reads the label and weighs it against
              their age, weight, body condition, health concerns and breed.
            </p>
            <ul className="nl-points">
              <li>
                <strong>A 5-star match, not a mystery score</strong> — with the
                exact profile fields used, shown right on the card.
              </li>
              <li>
                <strong>Allergy guard</strong> — if the recipe contains an
                ingredient your pet reacts to, the verdict is a hard “skip”, no
                matter how good the food looks.
              </li>
              <li>
                <strong>Honest confidence</strong> — a front-of-pack glance says
                “looks promising”; the confident call comes only after the
                ingredients are read.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ───────────────────────── CASE C — HONEST NO */}
      <section className="nl-case nl-case--tinted">
        <div className="nl-shell nl-case-grid">
          <div className="nl-case-copy" data-reveal>
            <p className="nl-kicker">Verdicts with reasons</p>
            <h2 className="nl-h2">
              It says no — <em>and tells you why.</em>
            </h2>
            <p className="nl-body">
              A beautiful bag of dog food is still dog food. When something is
              wrong for your pet, PawPlate says it plainly and explains itself
              in one sentence: species mismatch, wrong life stage, a missing
              complete-and-balanced statement, or a conflict with a condition.
            </p>
            <blockquote className="nl-quote">
              “This recipe is made for dogs — Milo is a cat.”
              <cite>— an actual PawPlate verdict</cite>
            </blockquote>
          </div>
          <div className="nl-case-stage" data-reveal>
            <Phone src={skipWhyShot} alt="Skip verdict explaining the food is made for dogs" tilt="right" />
          </div>
        </div>
      </section>

      {/* ───────────────────────── CASE D — COMPARE */}
      <section className="nl-case">
        <div className="nl-shell nl-case-grid nl-case-grid--flip">
          <div className="nl-case-stage" data-reveal>
            <Phone src={comparisonShot} alt="Side-by-side comparison of popular cat foods with tied first place" tilt="left" />
          </div>
          <div className="nl-case-copy" data-reveal>
            <p className="nl-kicker">Five cans, one shelf</p>
            <h2 className="nl-h2">
              Standing in the aisle,
              <br />
              phone in hand? <em>Compare.</em>
            </h2>
            <p className="nl-body">
              Snap two to five packages — Sheba, Friskies, Royal Canin,
              whatever the shelf offers — and get a ranked answer for your pet.
              Including the truth nobody else prints: when the options are
              practically equal, PawPlate says so instead of inventing a winner.
            </p>
            <ul className="nl-points">
              <li>
                <strong>Ranked for your pet</strong> — same personalized engine,
                across every product at once.
              </li>
              <li>
                <strong>“Why this pick”</strong> — the winner explains what it
                wins on: stronger ingredient list, fuller label, protein density.
              </li>
              <li>
                <strong>Saved automatically</strong> — every comparison lives in
                your history, ready to re-open at the next shopping trip.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ───────────────────────── HEALTH GRID */}
      <section className="nl-health">
        <div className="nl-shell">
          <div className="nl-health-head" data-reveal>
            <p className="nl-kicker">Vomit · stool · skin · eyes · 16 checks</p>
            <h2 className="nl-h2">
              Worried about something? <em>Scan it.</em>
            </h2>
            <p className="nl-body nl-body--center">
              The same camera triages what worries pet parents most. PawPlate
              tells you what it sees, how urgent it looks, and what to do now —
              in plain words, tuned to be calm when it can be and direct when it
              must be.
            </p>
          </div>
          <div className="nl-health-grid">
            {[
              {
                t: "An honest triage read",
                d: "Every result leads with a verdict band and a single “what to do now” — monitor at home, see a vet within days, or find one right now.",
              },
              {
                t: "It asks what photos can’t show",
                d: "Labored breathing, collapse, no urination — owner-observed red flags override everything and route you straight to emergency care.",
              },
              {
                t: "It remembers",
                d: "“Improved vs the scan 5 days ago.” Repeat episodes escalate automatically — the third vomit this week is treated differently than the first.",
              },
              {
                t: "It follows up",
                d: "A day after a “keep an eye on it” verdict, PawPlate checks in: how are they doing? Re-scan or log a symptom in one tap.",
              },
              {
                t: "It teaches",
                d: "Every health result can hand you exactly one relevant article — why cats vomit, what a healthy coat looks like. No feed, no noise.",
              },
              {
                t: "It knows its limits",
                d: "Low-confidence reads say so. Screening, not diagnosis — with vet-grade references (WSAVA, Bristol, PDSA) behind every check.",
              },
            ].map((f, i) => (
              <article className="nl-health-card" data-reveal key={i} style={{ "--stagger": `${i * 60}ms` }}>
                <span className="nl-health-num">{String(i + 1).padStart(2, "0")}</span>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── CASE E — VET CHAT */}
      <section className="nl-case">
        <div className="nl-shell nl-case-grid">
          <div className="nl-case-copy" data-reveal>
            <p className="nl-kicker">Messages · always on</p>
            <h2 className="nl-h2">
              A pet nutritionist
              <br />
              <em>in your pocket.</em>
            </h2>
            <p className="nl-body">
              Ask anything, anytime — “Milo threw up this morning, should I be
              worried?” — and get an answer that actually knows your pet: their
              meals, water intake, symptom log and scan history. It reassures
              when it can, and tells you clearly when it's time to see a vet.
            </p>
            <ul className="nl-points">
              <li>
                <strong>Knows the whole picture</strong> — answers reference your
                pet's real log, not generic advice.
              </li>
              <li>
                <strong>Reaches out first</strong> — noticed low water intake
                this week? It will mention it before you ask.
              </li>
              <li>
                <strong>Honest escalation</strong> — it never plays doctor; when
                a vet is needed, it says so and helps you act.
              </li>
            </ul>
          </div>
          <div className="nl-case-stage" data-reveal>
            <Phone src={vetChatShot} alt="PawPlate AI pet nutritionist chat answering a worried owner" tilt="right" />
          </div>
        </div>
      </section>

      {/* ───────────────────────── MULTIPET BAND */}
      <section className="nl-multipet">
        <div className="nl-shell nl-multipet-inner" data-reveal>
          <div className="nl-multipet-chips" aria-hidden="true">
            <span className="nl-petchip nl-petchip--active">🐱 Milo</span>
            <span className="nl-petchip">🐶 Luna</span>
            <span className="nl-petchip nl-petchip--add">＋</span>
          </div>
          <h2 className="nl-h2">
            More than one? <em>Every pet gets their own plan.</em>
          </h2>
          <p className="nl-body nl-body--center">
            Profiles, daily plans, food matches, scans and health history are
            kept separate for each of your pets. Switch in one tap — the whole
            app follows: Milo's food verdicts never mix with Luna's, and each
            plan grows with the pet it belongs to.
          </p>
        </div>
      </section>

      {/* ───────────────────────── FINAL CTA */}
      <section className="nl-final">
        <div className="nl-final-inner nl-shell" data-reveal>
          <h2 className="nl-final-title">
            Give your pet
            <br />
            the care they deserve.
          </h2>
          <p className="nl-final-sub">
            Free to start. Personal from the first day.
          </p>
          <StoreBadges size="lg" />
        </div>
      </section>

      {/* ───────────────────────── FOOTER */}
      <footer className="nl-footer">
        <div className="nl-shell nl-footer-inner">
          <a className="nl-brand nl-brand--footer" href="/">
            <img src={logo} alt="" className="nl-brand-mark" />
            <span className="nl-brand-word">PawPlate</span>
          </a>
          <nav className="nl-footer-links" aria-label="Legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-use">Terms of Use</Link>
            <Link to="/subscription-policy">Subscription Policy</Link>
            <Link to="/contact-us">Contact Us</Link>
          </nav>
          <p className="nl-footer-note">
            PawPlate offers screening guidance, not veterinary diagnosis. When
            in doubt, see your vet. © {new Date().getFullYear()} PawPlate.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NewLanding;
